import React from 'react';
import { motion } from "motion/react";
import { GroupedTicketsView } from './GroupedTicketsView';
import { CartGroupedTicketsView } from './CartGroupedTicketsView';
import { TransferTicketsView } from './TransferTicketsView';
import { EventSkeletons } from '@/components/skeleton';
import type { GroupedScheduleResponse, GroupedCartResponse } from '@/features/booking/types/bookingTypes';
import type { BookingTransfer } from '@/features/booking/types/transferTypes';

interface MyTicketsSectionProps {
  groupedByEventSchedules: GroupedScheduleResponse[];
  groupedByPastEventSchedules: GroupedScheduleResponse[];
  groupedByCarts: GroupedCartResponse[];
  allTransfers: BookingTransfer[];
  activeTab: 'by-event' | 'by-cart' | 'by-transfer';
  onTabChange: (tab: 'by-event' | 'by-cart' | 'by-transfer') => void;
  activeEventTab: 'upcoming' | 'past';
  onEventTabChange: (tab: 'upcoming' | 'past') => void;
  pastEventsLoading: boolean;
  loading?: boolean;
}

export const MyTicketsSection: React.FC<MyTicketsSectionProps> = ({
  groupedByEventSchedules,
  groupedByPastEventSchedules,
  groupedByCarts,
  allTransfers,
  activeTab,
  onTabChange,
  activeEventTab,
  onEventTabChange,
  pastEventsLoading,
  loading = false
}) => {
  return (
    <motion.div
      className="lg:col-span-2 space-y-6"
      initial={{ opacity: 0, x: 50, scale: 0.95 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: -50, scale: 0.95 }}
      transition={{ type: "spring", stiffness: 300, damping: 30, mass: 0.8 }}
    >
      <div className="bg-gray-900 border border-gray-800 rounded-3xl p-4 sm:p-6 lg:p-8 h-auto sm:h-[600px] lg:h-[700px] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-4 sm:mb-6 lg:mb-8 flex-shrink-0">
          <div className="flex-1">
            <h3 className="text-lg sm:text-xl lg:text-2xl font-serif font-medium mb-2">
              My Tickets
            </h3>
            <div className="h-0.5 bg-violet-500 mt-1 sm:mt-2 w-16" />
          </div>

          {/* Tabs */}
          <div className="flex border-b border-gray-700">
            <button
              onClick={() => onTabChange('by-event')}
              className={`px-4 sm:px-6 py-3 font-medium text-sm transition-colors relative ${activeTab === 'by-event'
                ? 'text-white border-b-2 border-violet-500'
                : 'text-gray-400 hover:text-gray-300'
                }`}
            >
              By Event
            </button>
            <button
              onClick={() => onTabChange('by-cart')}
              className={`px-4 sm:px-6 py-3 font-medium text-sm transition-colors relative ${activeTab === 'by-cart'
                ? 'text-white border-b-2 border-violet-500'
                : 'text-gray-400 hover:text-gray-300'
                }`}
            >
              By Cart
            </button>
            <button
              onClick={() => onTabChange('by-transfer')}
              className={`px-4 sm:px-6 py-3 font-medium text-sm transition-colors relative ${activeTab === 'by-transfer'
                ? 'text-white border-b-2 border-violet-500'
                : 'text-gray-400 hover:text-gray-300'
                }`}
            >
              By Transfer
            </button>
          </div>
        </div>

        {/* Content area */}
        <div className="flex-1 overflow-y-visible sm:overflow-y-auto pr-1 sm:pr-2">
          {loading ? (
            <EventSkeletons count={3} />
          ) : (
            <>
              {activeTab === 'by-event' && (
                <div className="space-y-4">
                  {/* Nested tabs for Upcoming/Past Events */}
                  <div className="flex border-b border-gray-700 w-fit">
                    <button
                      onClick={() => onEventTabChange('upcoming')}
                      className={`px-4 sm:px-6 py-3 font-medium text-sm transition-colors relative ${
                        activeEventTab === 'upcoming'
                          ? 'text-white border-b-2 border-violet-500'
                          : 'text-gray-400 hover:text-gray-300'
                      }`}
                    >
                      Upcoming Events
                    </button>
                    <button
                      onClick={() => onEventTabChange('past')}
                      className={`px-4 sm:px-6 py-3 font-medium text-sm transition-colors relative ${
                        activeEventTab === 'past'
                          ? 'text-white border-b-2 border-violet-500'
                          : 'text-gray-400 hover:text-gray-300'
                      }`}
                    >
                      Past Events
                    </button>
                  </div>

                  {/* Content based on nested tab */}
                  {activeEventTab === 'upcoming' && (
                    <GroupedTicketsView groupedSchedules={groupedByEventSchedules} />
                  )}
                  {activeEventTab === 'past' && (
                    pastEventsLoading ? (
                      <EventSkeletons count={3} />
                    ) : (
                      <GroupedTicketsView groupedSchedules={groupedByPastEventSchedules} />
                    )
                  )}
                </div>
              )}
              {activeTab === 'by-cart' && (
                <CartGroupedTicketsView groupedCarts={groupedByCarts} />
              )}
              {activeTab === 'by-transfer' && (
                <TransferTicketsView groupedSchedules={groupedByEventSchedules} allTransfers={allTransfers} />
              )}
            </>
          )}
        </div>
      </div>
    </motion.div>
  );
};
